// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this
// file, exactly as StageTrendChart.tsx and components/common/MonthlyBreakdownChart.tsx do.
import React, { useId } from 'react';
import {
  PieChart, Pie, Cell as PieCell, ResponsiveContainer, Tooltip,
  XAxis, YAxis, CartesianGrid, BarChart, Bar,
} from 'recharts';
import { money, num } from './pipelineParts';
import { CHART_AXIS_TICK, CHART_GRID, ChartTooltipCard, chartGradient } from './trackerKit';

/**
 * The phase 3 charts, drawn from report data only. No chart here holds a figure of its own: each
 * one is a picture of a table the section also prints, so a reader who cannot see the chart, or a
 * test that cannot render one, loses nothing.
 *
 * House kit throughout (trackerKit): CHART_GRID and CHART_AXIS_TICK, the dark ChartTooltipCard,
 * gradient fills from chartGradient, and no hover box on bars (the house rule).
 * The velocity trend lives in VelocityTrendChart.
 */

/**
 * One wheel. `data` is [{ label, value, colour }], already folded and already in the chosen unit.
 * Each segment fills with its own 'donut' gradient (the colour to a deeper shade of itself), so
 * neighbouring segments separate even where two hues sit close.
 */
export const Donut = ({ data, format, centre }) => {
  const uid = useId().replace(/[^a-z0-9]/gi, '');
  const gid = (i) => `donut-${uid}-${i}`;
  return (
    <div className="relative h-40 w-40 shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <defs>{data.map((d, i) => chartGradient(gid(i), d.colour, 'donut'))}</defs>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            innerRadius="62%"
            outerRadius="100%"
            stroke="#111827"
            strokeWidth={1}
            paddingAngle={data.length > 1 ? 1 : 0}
            isAnimationActive={false}
          >
            {data.map((d, i) => <PieCell key={d.label} fill={`url(#${gid(i)})`} />)}
          </Pie>
          <Tooltip
            content={({ active, payload }) => (
              <ChartTooltipCard
                active={active}
                payload={payload?.map((p) => ({ ...p, color: p.payload?.colour }))}
                format={format}
              />
            )}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-center text-sm font-semibold text-gray-100">
        {centre}
      </div>
    </div>
  );
};

/**
 * Planned settlements by month, stacked by tier.
 *
 * `unit` says what `values` are: integer cents (plotted as dollars) or deal counts. The chart
 * follows the section's $ / # toggle, so an axis never reads in dollars while the table beside it
 * shows deals.
 */
export const StackedMonths = ({ months, stacks, unit = 'cents' }) => {
  const rows = months.map((label, i) => {
    const row = { month: label };
    for (const s of stacks) row[s.key] = unit === 'cents' ? s.values[i] / 100 : s.values[i];
    return row;
  });
  const fmt = (v) => (unit === 'cents' ? money(Math.round(v * 100)) : num(Math.round(v)));
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="month" tick={CHART_AXIS_TICK} stroke="#344155" tickLine={false} />
          <YAxis tick={CHART_AXIS_TICK} stroke="#344155" tickLine={false} allowDecimals={unit === 'cents'} tickFormatter={fmt} />
          {/* BRAND RULE: never the default grey hover box behind a column (founder, 5 Oct 2026). No
              cursor at all here: the stacked tiers already separate the columns. */}
          <Tooltip cursor={false} content={<ChartTooltipCard format={fmt} />} />
          {stacks.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.name} stackId="tier" fill={s.colour} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
